export const LOCALE_STORAGE_KEY = "hei-creative-locale";

export const SUPPORTED_LOCALES = ["zh", "yue", "en"];

function normalizeLanguageTag(lang) {
    return (lang || "").toLowerCase().replace(/_/g, "-").trim();
}

/**
 * Map a single BCP 47 language tag to a supported locale.
 * Returns null when the tag matches none of the supported locales,
 * so the caller can move on to the user's next preference.
 *
 * Subtags are compared piecewise (not via startsWith), so variants with
 * extra script/region subtags such as "zh-Hant-TW" or "yue-Hant-HK" resolve correctly.
 */
export function resolveLocaleFromLanguageTag(lang) {
    const normalized = normalizeLanguageTag(lang);
    if (!normalized) {
        return null;
    }

    const subtags = normalized.split("-");
    const primary = subtags[0];

    if (primary === "yue") {
        return "yue";
    }
    if (primary === "en") {
        return "en";
    }
    if (primary !== "zh") {
        return null;
    }

    // Cantonese-speaking regions: Hong Kong / Macau
    if (subtags.includes("hk") || subtags.includes("mo")) {
        return "yue";
    }

    // Traditional script (zh-TW, zh-Hant, zh-Hant-*): no dedicated locale,
    // degrade to "yue" (rendered as zh-HK) to keep a Traditional Chinese presentation
    // instead of forcing Simplified.
    if (subtags.includes("hant") || subtags.includes("tw")) {
        return "yue";
    }

    // Simplified Chinese: zh, zh-CN, zh-SG, zh-Hans, zh-Hans-*
    return "zh";
}

export function getDocumentLang(locale) {
    if (locale === "zh") {
        return "zh-CN";
    }
    if (locale === "yue") {
        return "zh-HK";
    }
    return "en";
}

export function detectLocale() {
    if (typeof window !== "undefined") {
        const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
        if (SUPPORTED_LOCALES.includes(stored)) {
            return stored;
        }
    }

    const browserLanguages =
        typeof navigator !== "undefined"
            ? navigator.languages?.length
                ? [...navigator.languages]
                : [navigator.language || navigator.userLanguage || "en"]
            : ["en"];

    // Single pass in user preference order (BCP 47): the first tag that
    // resolves to a supported locale wins.
    for (const lang of browserLanguages) {
        const resolved = resolveLocaleFromLanguageTag(lang);
        if (resolved) {
            return resolved;
        }
    }

    return "en";
}
